import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/app-store';
import { motion, AnimatePresence } from 'motion/react';
import { BookOpen, Check, Loader2, Sparkles, PartyPopper, Lock } from 'lucide-react';
import { exportToPDF, downloadBlob } from '../utils/export-pdf';
import { exportToEPUB } from '../utils/export-epub';
import { exportToDocx } from '../utils/export-docx';
import { exportToHtml } from '../utils/export-html';
import { api } from '../lib/api';
import { notify } from '../lib/toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';
import type { ExportOptions } from '../types';

// Word target used across the app to derive a book's completion percentage
// (dashboard, bookshelf, book detail drawer). Kept in sync here so the
// progress bar shown next to the book means the same thing everywhere.
const NOVEL_TARGET = 80_000;

export function ExportView() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projects = useAppStore((s) => s.projects);

  const project = projects.find((p) => p.id === projectId);

  useEffect(() => {
    if (!project && projectId) {
      navigate('/app/projects');
    }
  }, [project, projectId, navigate]);

  const [options, setOptions] = useState<ExportOptions>({
    format: 'pdf',
    includeTitle: true,
    includeToc: true,
    includeCopyright: true,
    includeCover: true,
    printReady: false,
    pageSize: '6x9',
    fontSize: 'medium',
    fontFamily: 'serif',
    lineSpacing: '1.5',
    margins: 'normal',
    chapterStartPage: 'right',
    includePageNumbers: true,
    dropCaps: true,
    headerFooter: 'chapter',
  });

  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);

  // EPUB/HTML export is gated behind the Craft add-on (see addons-pricing.tsx).
  // Export itself runs entirely client-side (no server route to enforce this
  // against), so this is a best-effort client gate backed by the server's
  // computed entitlements (GET /addons). null = not loaded yet — fail-open so
  // a transient network hiccup never blocks a legitimately entitled user.
  const [craftUnlocked, setCraftUnlocked] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.getAddons()
      .then((res) => { if (!cancelled) setCraftUnlocked(res.entitlements.craft); })
      .catch(() => { /* best-effort — see comment above */ });
    return () => { cancelled = true; };
  }, []);
  const CRAFT_GATED_FORMATS: ReadonlyArray<ExportOptions['format']> = ['epub', 'html'];
  const isCraftGated = (format: ExportOptions['format']) => CRAFT_GATED_FORMATS.includes(format);
  const isLocked = (format: ExportOptions['format']) => isCraftGated(format) && craftUnlocked === false;

  // cerimônia do lacre quando a exportação conclui
  useEffect(() => {
    if (!success) return;
    window.dispatchEvent(new CustomEvent('reformu:moment', {
      detail: { type: 'seal', title: project?.metadata.title || t('projects.untitled', 'Sem título') },
    }));
  }, [success]);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (formatOverride?: ExportOptions['format']) => {
    if (!project) return;

    const activeFormat = formatOverride ?? options.format;

    if (isLocked(activeFormat)) {
      notify.addonRequired(
        'craft',
        t('export.craftRequiredDescription', 'Exportar em EPUB e HTML faz parte do add-on Craft.'),
      );
      return;
    }
    if (formatOverride && formatOverride !== options.format) {
      setOptions((o) => ({ ...o, format: formatOverride }));
    }

    setExporting(true);
    setError(null);
    setSuccess(false);
    setProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(p + Math.random() * 15, 90));
    }, 200);

    try {
      const safeTitle = (project.metadata.title || 'book').replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `${safeTitle}.${activeFormat}`;
      // Use the exact format being exported — options.format may not have
      // flushed to state yet within this same call when triggered via a
      // format card click, so we build the export config explicitly.
      const exportOptions: ExportOptions = { ...options, format: activeFormat };

      let blob: Blob;
      if (activeFormat === 'pdf') {
        blob = await exportToPDF(project, exportOptions);
      } else if (activeFormat === 'epub') {
        blob = await exportToEPUB(project, exportOptions);
      } else if (activeFormat === 'html') {
        blob = exportToHtml(project, exportOptions);
      } else {
        blob = await exportToDocx(project, exportOptions);
      }

      clearInterval(progressInterval);
      setProgress(100);

      await new Promise(resolve => setTimeout(resolve, 300));
      downloadBlob(blob, filename);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      console.error('Export error:', err);
      setError(t('export.error'));
    } finally {
      clearInterval(progressInterval);
      setExporting(false);
      setProgress(0);
    }
  };

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalWords = project.chapters.reduce((sum, ch) => sum + ch.wordCount, 0);
  const progressPct = Math.min(100, Math.round((totalWords / NOVEL_TARGET) * 100));

  const formats: Array<{ id: ExportOptions['format']; title: string; description: string }> = [
    { id: 'pdf', title: 'PDF', description: 'Print & KDP' },
    { id: 'epub', title: 'EPUB', description: 'eReaders' },
    { id: 'docx', title: 'DOCX', description: 'Word' },
    { id: 'html', title: 'HTML', description: t('exportView.htmlFormatDescription', 'Página web do livro') },
  ];

  return (
    <div className="flex-1 p-4 md:p-8 max-w-3xl mx-auto w-full overflow-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="m-eyebrow">{t('editorView.exportPublish', 'Exportar / Publicar')}</p>
          <h1 className="font-serif font-medium text-foreground mt-1.5" style={{ fontSize: '22px' }}>
            {t('export.title', 'Export Your Book')}
          </h1>
        </div>

        <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-aurora/30 bg-aurora/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-aurora">
          <Sparkles size={12} />
          {t('export.kdpReady', 'KDP Ready')}
        </span>
      </div>

      {/* Book preview + info */}
      <div className="grid grid-cols-[96px_1fr] md:grid-cols-[150px_1fr] gap-3.5 md:gap-6 items-start mb-6">
        <div
          className="relative overflow-hidden rounded-[8px_12px_12px_8px] flex-shrink-0"
          style={{
            aspectRatio: '5 / 7.4',
            boxShadow: '0 24px 44px -16px rgba(0,0,0,.7), 0 0 0 1px rgba(168,184,232,.25) inset',
          }}
        >
          <div className="absolute left-2 top-0 bottom-0 w-[1.5px] bg-white/20 z-10" aria-hidden="true" />
          {project.metadata.coverImage ? (
            <img
              src={project.metadata.coverImage}
              alt="Book cover"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-2.5 text-center bg-gradient-to-br from-primary/20 via-primary/30 to-primary/40">
              <BookOpen className="h-6 w-6 md:h-8 md:w-8 text-primary/60" />
              <p className="font-serif text-foreground text-[11px] md:text-xs leading-tight line-clamp-3">
                {project.metadata.title || t('projects.untitled', 'Untitled')}
              </p>
            </div>
          )}
        </div>

        <div className="min-w-0 pt-0.5">
          <b className="font-serif font-medium text-[19px] text-foreground block leading-snug">
            {project.metadata.title || t('projects.untitled', 'Untitled')}
          </b>
          {project.metadata.author && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{project.metadata.author}</p>
          )}
          <p className="m-eyebrow mt-1.5">
            {(totalWords / 1000).toFixed(1)}k {t('stats.words', 'Words')} · {project.chapters.length} {t('stats.chapters', 'Chapters')} · {progressPct}%
          </p>
          <div className="xp-track mt-2.5 max-w-[220px]">
            <div className="xp-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="mt-2.5 text-[11px] text-muted-foreground leading-relaxed max-w-[38ch]">
            {t('export.subtitle', 'Choose format and customize settings')}
          </p>
        </div>
      </div>

      {/* Format cards — clicking exports directly in that format */}
      <p className="sh-l mt-0">{t('export.format', 'Format')}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {formats.map((format) => {
          const isExportingThis = exporting && options.format === format.id;
          const isSuccessThis = success && options.format === format.id;
          return (
            <button
              key={format.id}
              type="button"
              onClick={() => handleExport(format.id)}
              disabled={exporting}
              title={`${t('export.exportButton', 'Export')} ${format.title}`}
              aria-label={`${t('export.exportButton', 'Export')} ${format.title}`}
              className={cn(
                'fmt relative disabled:cursor-not-allowed',
                exporting && !isExportingThis && 'opacity-40 pointer-events-none',
              )}
            >
              {isLocked(format.id) && (
                <span
                  className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-muted text-muted-foreground"
                  title={t('export.craftRequiredBadge', 'Requer add-on Craft')}
                  aria-hidden="true"
                >
                  <Lock size={9} />
                </span>
              )}
              {isExportingThis ? (
                <Loader2 className="w-5 h-5 mb-2 animate-spin text-ember-hot" aria-hidden="true" />
              ) : isSuccessThis ? (
                <Check className="w-5 h-5 mb-2 text-aurora" aria-hidden="true" />
              ) : (
                <i aria-hidden="true">⇩</i>
              )}
              <b>{format.title}</b>
              <small>
                {isExportingThis
                  ? `${t('export.exporting', 'Exporting...')} ${Math.round(progress)}%`
                  : isSuccessThis
                    ? t('export.success', 'Download Started!')
                    : format.description}
              </small>
            </button>
          );
        })}
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-3 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success message */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-3 flex items-center gap-2 rounded-lg border border-aurora/30 bg-aurora/10 px-4 py-3 text-aurora text-sm"
          >
            <PartyPopper className="h-4 w-4 flex-shrink-0" />
            {t('export.success', 'Download Started!')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Format tip */}
      <p className="mt-3 text-[11px] text-muted-foreground text-center">
        {options.format === 'pdf' && t('export.pdfTip', 'Optimized for Amazon KDP print-on-demand')}
        {options.format === 'epub' && t('export.epubTip', 'Works with Kindle, Apple Books, and more')}
        {options.format === 'docx' && t('export.docxTip', 'Editable in Microsoft Word and Google Docs')}
        {options.format === 'html' && t('export.htmlTip', 'Self-contained web page, open in any browser')}
      </p>

      {/* Settings */}
      <div className="mt-6 space-y-4">
        {/* Content options */}
        <div className="dcard">
          <p className="dcard-t">{t('export.contentOptions', 'Content')}</p>
          <div className="space-y-1">
            <ExportToggle
              label={t('export.includeTitle', 'Include Title Page')}
              checked={options.includeTitle}
              onChange={(checked) => setOptions(o => ({ ...o, includeTitle: checked }))}
            />
            <ExportToggle
              label={t('export.includeToc', 'Include Table of Contents')}
              checked={options.includeToc}
              onChange={(checked) => setOptions(o => ({ ...o, includeToc: checked }))}
            />
            <ExportToggle
              label={t('export.includeCopyright', 'Include Copyright Page')}
              checked={options.includeCopyright}
              onChange={(checked) => setOptions(o => ({ ...o, includeCopyright: checked }))}
            />
            {project.metadata.coverImage && (
              <ExportToggle
                label={t('export.includeCover', 'Include Cover Image')}
                checked={options.includeCover}
                onChange={(checked) => setOptions(o => ({ ...o, includeCover: checked }))}
              />
            )}
            {options.format === 'pdf' && (
              <ExportToggle
                label={t('export.pageNumbers', 'Include Page Numbers')}
                checked={options.includePageNumbers}
                onChange={(checked) => setOptions(o => ({ ...o, includePageNumbers: checked }))}
              />
            )}
            {options.format === 'pdf' && (
              <ExportToggle
                label={t('export.dropCaps', 'Capitular no início do capítulo')}
                checked={options.dropCaps}
                onChange={(checked) => setOptions(o => ({ ...o, dropCaps: checked }))}
              />
            )}
          </div>
        </div>

        {/* Layout options */}
        {options.format === 'pdf' && (
          <div className="dcard">
            <p className="dcard-t">{t('export.layoutOptions', 'Layout')}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="sh-l block">{t('export.pageSize', 'Page Size')}</Label>
                <Select
                  value={options.pageSize}
                  onValueChange={(value) => setOptions(o => ({ ...o, pageSize: value as ExportOptions['pageSize'] }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6x9">6 × 9 in (KDP Standard)</SelectItem>
                    <SelectItem value="5x8">5 × 8 in (Digest)</SelectItem>
                    <SelectItem value="5.5x8.5">5.5 × 8.5 in (US Trade)</SelectItem>
                    <SelectItem value="a5">A5 (148 × 210 mm)</SelectItem>
                    <SelectItem value="a4">A4 (210 × 297 mm)</SelectItem>
                    <SelectItem value="letter">US Letter (8.5 × 11 in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="sh-l block">{t('export.margins', 'Margins')}</Label>
                <Select
                  value={options.margins}
                  onValueChange={(value) => setOptions(o => ({ ...o, margins: value as ExportOptions['margins'] }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="narrow">{t('export.narrow', 'Narrow')}</SelectItem>
                    <SelectItem value="normal">{t('export.normal', 'Normal')}</SelectItem>
                    <SelectItem value="wide">{t('export.wide', 'Wide')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="sh-l block">{t('export.chapterStart', 'Chapter Start')}</Label>
                <Select
                  value={options.chapterStartPage}
                  onValueChange={(value) => setOptions(o => ({ ...o, chapterStartPage: value as ExportOptions['chapterStartPage'] }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t('export.anyPage', 'Any Page')}</SelectItem>
                    <SelectItem value="right">{t('export.rightPage', 'Right Page (Odd)')}</SelectItem>
                    <SelectItem value="left">{t('export.leftPage', 'Left Page (Even)')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="sh-l block">{t('export.headerFooter', 'Header/Footer')}</Label>
                <Select
                  value={options.headerFooter}
                  onValueChange={(value) => setOptions(o => ({ ...o, headerFooter: value as ExportOptions['headerFooter'] }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common.none', 'None')}</SelectItem>
                    <SelectItem value="title">{t('export.bookTitle', 'Book Title')}</SelectItem>
                    <SelectItem value="author">{t('export.authorName', 'Author Name')}</SelectItem>
                    <SelectItem value="chapter">{t('export.chapterTitle', 'Chapter Title')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Typography options */}
        <div className="dcard">
          <p className="dcard-t">{t('export.typographyOptions', 'Typography')}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="sh-l block">{t('export.fontSize', 'Font Size')}</Label>
              <Select
                value={options.fontSize}
                onValueChange={(value) => setOptions(o => ({ ...o, fontSize: value as ExportOptions['fontSize'] }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{t('export.small', 'Small')} (10pt)</SelectItem>
                  <SelectItem value="medium">{t('export.medium', 'Medium')} (11pt)</SelectItem>
                  <SelectItem value="large">{t('export.large', 'Large')} (12pt)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="sh-l block">{t('export.fontFamily', 'Font Family')}</Label>
              <Select
                value={options.fontFamily}
                onValueChange={(value) => setOptions(o => ({ ...o, fontFamily: value as ExportOptions['fontFamily'] }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="serif">Serif (Georgia)</SelectItem>
                  <SelectItem value="sans">Sans-serif (Helvetica)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label className="sh-l block">{t('export.lineSpacing', 'Line Spacing')}</Label>
              <Select
                value={options.lineSpacing}
                onValueChange={(value) => setOptions(o => ({ ...o, lineSpacing: value as ExportOptions['lineSpacing'] }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">{t('export.single', 'Single')}</SelectItem>
                  <SelectItem value="1.5">1.5 {t('export.lines', 'Lines')}</SelectItem>
                  <SelectItem value="double">{t('export.double', 'Double')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Success Confetti Animation */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-50"
          >
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-3 h-3 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  backgroundColor: ['#f59e0b', '#5EEAD4', '#3b82f6', '#ec4899', '#8b5cf6'][i % 5],
                }}
                initial={{ top: '-5%', opacity: 1 }}
                animate={{
                  top: '105%',
                  opacity: 0,
                  rotate: Math.random() * 360,
                  x: (Math.random() - 0.5) * 200,
                }}
                transition={{
                  duration: 2 + Math.random(),
                  delay: Math.random() * 0.5,
                  ease: 'easeOut',
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ExportToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-1">
      <span className="text-foreground text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
